import PasswordInput from '../components/PasswordInput';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, AuthApi } from '../api';
import PasswordResetModal from '../components/PasswordResetModal';
import Logo from '../components/Logo';
import { clearSession, notifySessionChanged } from '../auth/session';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordResetMode, setPasswordResetMode] = useState<'request' | 'reset' | 'change'>('request');
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const login = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    try {
      const res = await AuthApi.login({ username, password });
      const token = res.data.token;
      const role = res.data.role;
      const firstLogin = res.data.firstLogin;
      
      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      notifySessionChanged();

      // Check if this is first login and force password reset
      if (firstLogin) {
        setPasswordResetEmail(username);
        setPasswordResetMode('change');
        setIsFirstLogin(true);
        setShowPasswordReset(true);
        return;
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      navigate('/');
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.code === 'ACCOUNT_INACTIVE') {
        clearSession();
        sessionStorage.setItem('inactiveAccount', JSON.stringify({
          username: err.response.data.username,
          email: err.response.data.email,
          fullName: err.response.data.fullName
        }));
        navigate('/account-inactive', { replace: true });
      } else if (err.response?.status === 403 && err.response?.data?.message === 'PASSWORD_RESET_REQUIRED') {
        setPasswordResetEmail(username);
        setPasswordResetMode('change');
        setIsFirstLogin(true);
        setShowPasswordReset(true);
      } else {
        setError(err.response?.data?.message || t('auth.loginError'));
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
    }}>
      <form
        onSubmit={login}
        style={{
          background: '#fff',
          padding: '40px 32px',
          borderRadius: 16,
          boxShadow: '0 4px 32px #1976d233',
          minWidth: 320,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Logo width={80} height={80} style={{ marginBottom: 16 }} />
        <h1 style={{ color: '#1559b0', marginBottom: 8, fontWeight: 700, fontSize: 28, display: 'flex', alignItems: 'center', gap: 8 }}>
          {t('app.title')}
          <span style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.8,
            color: '#0d47a1',
            background: '#fff3cd',
            border: '1px solid #ffe082',
            borderRadius: 999,
            padding: '2px 8px',
            lineHeight: 1.2
          }}>BETA</span>
        </h1>
        <p style={{ color: '#666', marginBottom: 24, fontSize: 16 }}>
          {t('app.subtitle')}
        </p>
        <h2 style={{ color: '#1976d2', marginBottom: 24, fontWeight: 700 }}>{t('auth.loginTitle')}</h2>
        
        {error && (
          <div style={{
            color: '#b71c1c',
            background: '#ffebee',
            border: '1px solid #ef9a9a',
            padding: '8px 12px',
            borderRadius: 4,
            marginBottom: 16,
            width: '100%',
            textAlign: 'center',
            fontSize: 14
          }}>
            {error}
          </div>
        )}
        
        <input
          placeholder={t('common.email')}
          value={username}
          onChange={e => setUsername(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            marginBottom: 16,
            borderRadius: 8,
            border: '1px solid #1976d2',
            fontSize: 16
          }}
          autoFocus
        />
        <PasswordInput
          placeholder={t('common.password')}
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            marginBottom: 24,
            borderRadius: 8,
            border: '1px solid #1976d2',
            fontSize: 16
          }}
        />
        <button
          type="submit"
          style={{
            width: '100%',
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 0',
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px #1976d233',
            marginBottom: 16
          }}
        >
          {t('auth.loginButton')}
        </button>

        <button
          type="button"
          onClick={() => {
            setPasswordResetMode('request');
            setPasswordResetEmail('');
            setIsFirstLogin(false);
            setShowPasswordReset(true);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#1976d2',
            cursor: 'pointer',
            fontSize: 14,
            textDecoration: 'underline',
            marginBottom: 16
          }}
        >
          {t('passwordReset.forgotPassword')}
        </button>

        <div style={{ color: '#666', fontSize: 14, textAlign: 'center' }}>
          {t('auth.noAccount')}{' '}
          <Link to="/register" style={{ color: '#1976d2', fontWeight: 700, textDecoration: 'none' }}>
            {t('auth.createAccount')}
          </Link>
        </div>
      </form>

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        mode={passwordResetMode}
        email={passwordResetEmail}
        isFirstLogin={isFirstLogin}
        onSuccess={() => {
          if (passwordResetMode === 'change') {
            // After forced password change, redirect to main page
            navigate('/');
          }
        }}
      />
    </div>
  );
}
